import React, { useState, useEffect } from 'react';
import { Save, User, Link, CheckCircle, ExternalLink } from 'lucide-react';
import authService from '../services/AuthService';
import SubscriptionManagement from '../components/SubscriptionManagement';
import WalmartLoginButton from './MSPInventory/components/walmart/Walmartloginbutton';
import { API_BASE } from '../consts';

const SettingsPage = ({ onSettingsUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Affiliate
    const [myAffLink, setMyAffLink] = useState('');
    const [affliateStats, setAffiliateStats] = useState(null);
    const [copiedAffLink, setCopiedAffLink] = useState(false);
    const [affSearchQuery, setAffSearchQuery] = useState('');

    // Settings
    const AMAZON_MARKETPLACES = [
        { code: 'US', domain: '.com', flag: '🇺🇸', label: 'United States' },
        { code: 'UK', domain: '.co.uk', flag: '🇬🇧', label: 'United Kingdom' },
        { code: 'CA', domain: '.ca', flag: '🇨🇦', label: 'Canada' },
        { code: 'MX', domain: '.com.mx', flag: '🇲🇽', label: 'Mexico' },
    ];

    const [settings, setSettings] = useState({
        email: '',
        marketplaces_sell_on: [],
        business_interests: [],
        how_found_us: '',
        amazon_marketplace: 'US'
    });

    // Walmart linking (OAuth handled entirely by WalmartLoginButton)
    const [walmartLinked, setWalmartLinked] = useState(false);

    // Amazon
    const [amazonLinked, setAmazonLinked] = useState(false);

    const marketplaces = ['Amazon', 'Walmart', 'eBay', 'AIP'];

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (!loading) {
            if (window.location.hash === '#marketplaces') {
                setTimeout(() => {
                    document.getElementById('marketplaces-section')?.scrollIntoView({ behavior: 'smooth' });
                    setTimeout(() => window.scrollBy({ top: -70, behavior: 'smooth' }), 500);
                }, 500);
            }
        }
    }, [loading]);

    const loadSettings = async () => {
        try {
            const response = await authService.authenticatedFetch(`${API_BASE}/api/user/settings`);
            const data = await response.json();
            setSettings({
                email: data.email || '',
                marketplaces_sell_on: data.marketplaces_sell_on || [],
                business_interests: data.business_interests || [],
                how_found_us: data.how_found_us || ''
            });
            setMyAffLink(data.affiliate_link);
            setAffiliateStats(data.affiliate_stats);
            setWalmartLinked(!!data.walmart_linked_account && Object.keys(data.walmart_linked_account).length > 0);
            setSettings(prev => ({ ...prev, amazon_marketplace: data.amazon_marketplace || 'US' }));
            setAmazonLinked(!!data.amazon_linked_account && Object.keys(data.amazon_linked_account).length > 0);
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyAffLink = () => {
        navigator.clipboard.writeText(myAffLink);
        setCopiedAffLink(true);
        setTimeout(() => setCopiedAffLink(false), 2000);
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm('Are you sure you want to delete your account? This cannot be undone.')) return;
        if (!window.confirm('Last chance - permanently delete your account?')) return;
        await authService.authenticatedFetch(`${API_BASE}/api/user/delete-account`, { method: 'DELETE' });
        authService.logout();
        window.location.href = '/';
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            const response = await authService.authenticatedFetch(`${API_BASE}/api/user/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaces_sell_on: settings.marketplaces_sell_on,
                    business_interests: settings.business_interests,
                    how_found_us: settings.how_found_us,
                    amazon_marketplace: settings.amazon_marketplace
                })
            });
            if (response.ok) {
                setMessage('Settings saved successfully!');
                setTimeout(() => setMessage(''), 3000);
                if (onSettingsUpdated) onSettingsUpdated();
            }
        } catch (error) {
            setMessage('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const toggleSelection = (field, value) => {
        setSettings(prev => {
            const currentArray = prev[field] || [];
            return {
                ...prev,
                [field]: currentArray.includes(value)
                    ? currentArray.filter(item => item !== value)
                    : [...currentArray, value]
            };
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-bold text-gray-800 mb-8">Settings</h1>

                {message && (
                    <div className={`mb-6 p-4 rounded-lg ${message.includes('success') || message.includes('linked')
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                        {message}
                    </div>
                )}

                {/* Account Info */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <User className="text-teal-600" size={24} />
                        <h2 className="text-2xl font-bold text-gray-800">Account Information</h2>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                            type="email"
                            value={settings.email}
                            disabled
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        />
                    </div>
                    <a className="block text-sm font-medium text-gray-700 mb-2 mt-6" href="https://www.multisellerpro.com/forgot-password">Click here to reset your password</a>
                </div>

                {/* Affiliate Link */}
                <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                    <h2 className="text-xl font-bold mb-4">Your Affiliate Link</h2>
                    <div className="flex gap-2">
                        <input
                            value={myAffLink}
                            readOnly
                            className="border p-2 rounded flex-1 bg-gray-50"
                        />
                        <button
                            onClick={copyAffLink}
                            className="bg-teal-600 text-white px-4 py-2 rounded"
                        >
                            {copiedAffLink ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                        Share this link to earn commissions on referrals (30% recurring!!)
                    </p>
                </div>

                {/* Affiliate Stats */}
                {affliateStats && (
                    <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                        <h2 className="text-xl font-bold mb-4">Your Affiliate Stats</h2>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="bg-gray-50 p-4 rounded">
                                <div className="text-gray-500 text-sm">Signups</div>
                                <div className="text-2xl font-bold">{affliateStats.signups}</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded">
                                <div className="text-green-600 text-sm">Active</div>
                                <div className="text-2xl font-bold text-green-600">
                                    {affliateStats.active}{affliateStats.is_trial > 0 && <>&nbsp;<sub>({affliateStats.is_trial} in trial)</sub></>}
                                </div>
                            </div>
                            <div className="bg-blue-50 p-4 rounded">
                                <div className="text-blue-600 text-sm">Revenue</div>
                                <div className="text-2xl font-bold text-blue-600">${affliateStats.revenue}</div>
                            </div>
                        </div>
                        {affliateStats.users.length > 0 && (
                            <div>
                                <input
                                    type="text"
                                    placeholder="Search by email..."
                                    value={affSearchQuery}
                                    onChange={(e) => setAffSearchQuery(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                />
                                <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="p-3 text-left text-sm">Email</th>
                                                <th className="p-3 text-left text-sm">Plan</th>
                                                <th className="p-3 text-left text-sm">Status</th>
                                                <th className="p-3 text-left text-sm">Is Trial</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {affliateStats.users
                                                .filter(u => u.email.toLowerCase().includes(affSearchQuery.toLowerCase()))
                                                .map((u, i) => (
                                                    <tr key={i}>
                                                        <td className="p-3 text-sm">{u.email}</td>
                                                        <td className="p-3 text-sm">{u.plan || 'No Plan'}</td>
                                                        <td className="p-3 text-sm">{u.active ? '✅ Active' : '❌ Inactive'}</td>
                                                        <td className="p-3 text-sm">{u.is_trial ? '✅' : '❌'}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Linked Marketplace Accounts */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Link className="text-teal-600" size={24} />
                        <h2 className="text-2xl font-bold text-gray-800">Linked Marketplace Accounts</h2>
                    </div>

                    <div className="flex flex-col gap-4">

                        {/* Amazon */}
                        <div className="flex items-center justify-between border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <img
                                    src="/icons/amazon.png"
                                    alt="Amazon"
                                    className="w-10 h-10 object-contain rounded"
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                                <div>
                                    <div className="font-semibold text-gray-800">Amazon Seller Central</div>
                                    <div className="text-xs text-gray-500">Used for gating checks &amp; restriction lookups</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {amazonLinked ? (
                                    <span className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                                        <CheckCircle size={16} /> Connected
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => {
                                            window.open(
                                                `https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${settings.email}`,
                                                '_blank'
                                            );
                                        }}
                                        className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-semibold text-sm px-4 py-2 rounded-lg transition"
                                    >
                                        <ExternalLink size={14} />
                                        Login with Amazon
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Walmart */}
                        <div className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <img
                                        src="/icons/walmart.png"
                                        alt="Walmart"
                                        className="w-10 h-10 object-contain rounded"
                                        onError={e => { e.target.style.display = 'none'; }}
                                    />
                                    <div>
                                        <div className="font-semibold text-gray-800">Walmart Seller Center</div>
                                        <div className="text-xs text-gray-500">Used to view your orders &amp; catalog items &amp; MSP Inventory</div>
                                    </div>
                                </div>

                                <div className="flex-2">
                                    <WalmartLoginButton onStatusChange={(connected) => setWalmartLinked(connected)} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marketplaces */}
                <div id="marketplaces-section" className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Marketplaces You Sell On</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {marketplaces.map((marketplace) => (
                            <button
                                key={marketplace}
                                onClick={() => toggleSelection('marketplaces_sell_on', marketplace)}
                                className={`p-3 border-2 rounded-lg font-semibold transition flex flex-col items-center justify-center gap-1 ${(settings.marketplaces_sell_on || []).includes(marketplace)
                                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                                    : 'border-gray-300 hover:border-gray-400'
                                    }`}
                            >
                                <img
                                    src={`/icons/${marketplace.toLowerCase().replace(' shop', '').replace(/\s+/g, '-')}.png`}
                                    alt=""
                                    className="h-8 w-8 object-contain flex-shrink-0"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Amazon Marketplace Region */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-1">Amazon Marketplace Region</h2>
                    <p className="text-sm text-gray-500 mb-4">Select which Amazon marketplace you sell on. This affects product lookups and deal sourcing.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {AMAZON_MARKETPLACES.map((mp) => (
                            <button
                                key={mp.code}
                                onClick={() => setSettings(prev => ({ ...prev, amazon_marketplace: mp.code }))}
                                className={`p-4 border-2 rounded-xl font-semibold transition flex flex-col items-center gap-1 ${settings.amazon_marketplace === mp.code
                                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                                    : 'border-gray-200 hover:border-gray-400 text-gray-600'
                                    }`}
                            >
                                <span className="text-3xl">{mp.flag}</span>
                                <span className="text-sm font-bold">{mp.label}</span>
                                <span className="text-xs text-gray-400">amazon{mp.domain}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <SubscriptionManagement />

                <br />

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-4 rounded-xl text-lg font-semibold hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <Save size={20} />
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>

                <button
                    onClick={handleDeleteAccount}
                    className="mt-12 text-red-500 rounded-xl font-semibold hover:bg-red-50 transition"
                >
                    Delete Account
                </button>
            </div>
        </div>
    );
};

export default SettingsPage;